import { forgetChat } from "@codeshare/chat";
import { useEffect, useMemo, useSyncExternalStore } from "react";
import { BUILD_INFO } from "../build-info";
import { getChatSession } from "../chat/use-chat";
import { RUNNERS } from "../run/runners";
import { getTerminalSharing } from "../run/use-run";
import { getWorkspaceSession } from "../workspace/use-workspace";
import { getIdentityStore } from "./identity";
import { RoomController } from "./room-controller";
import { canUseRooms } from "./secure";

function pageBase(): string {
  return `${location.origin}${location.pathname}${location.search}`;
}

// One room controller per page.
let controller: RoomController | undefined;

export function getRoomController(): RoomController {
  controller ??= new RoomController({
    // `__codeshareTestApp` lets E2E tests impersonate another build in hello (never set in use).
    app: {
      ...BUILD_INFO,
      origin: location.origin,
      ...(globalThis as { __codeshareTestApp?: Partial<typeof BUILD_INFO> }).__codeshareTestApp,
    },
    profile: getIdentityStore().get(),
    // What this build can do: run these languages, and relay guest input into a run.
    caps: [...Object.keys(RUNNERS).map((id) => `run:${id}`), "term:input"],
    baseUrl: pageBase(),
    setFragment: (fragment) => history.replaceState(null, "", `${pageBase()}${fragment}`),
    // The workspace follows the room: sharing hands it over; joining loads the room's document.
    onJoined: async (room, created) => {
      const workspace = getWorkspaceSession();
      const topic = room.topic ?? "";
      if (created) await workspace.shareWith(room, topic);
      else await workspace.joinRoom(room, topic);
      await getChatSession().join(room, topic);
      getTerminalSharing().attachRoom(room);
    },
    onLeft: () => {
      getWorkspaceSession().leaveRoom();
      getChatSession().leave();
      getTerminalSharing().detachRoom();
    },
    onForgotten: async (topic) => {
      getChatSession().leave();
      getTerminalSharing().detachRoom();
      await Promise.all([getWorkspaceSession().forgetRoom(topic), forgetChat(topic)]);
    },
  });
  return controller;
}

export function useRoom() {
  const rooms = useMemo(getRoomController, []);
  const state = useSyncExternalStore(
    (listener) => rooms.subscribe(listener),
    () => rooms.state,
  );

  useEffect(() => {
    // A plain URL starts a room right away, so the address bar always holds a shareable link.
    void rooms.joinFromUrl(location.href).then((joined) => {
      if (!joined && canUseRooms()) void rooms.share();
    });
    const onHashChange = () => void rooms.joinFromUrl(location.href);
    // Closing or reloading the tab: say bye and close the connections now, so the others see
    // you leave right away (the room and its link stay as they are).
    const onPageHide = (event: PageTransitionEvent) => {
      if (!event.persisted) void rooms.room?.leave();
    };
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [rooms]);

  return {
    state,
    controller: rooms,
    share: () => rooms.share(),
    restart: () => rooms.restart(),
    leave: () => rooms.leave(),
    forget: () => rooms.leave({ forget: true }),
  };
}
